import '../settings/settings-leaf.css';
import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COLOR_PALETTE,
  COLOR_RULE_TYPES,
  colorForeground,
  normalizeColorValue,
  type ScheduleColors,
} from '@shared/schedule-colors.ts';
import { api } from '../../api/client.ts';
import { savedTeamsApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { useColorCopy } from './visit-colors.tsx';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextInput } from '../ui.tsx';
type Rule = ScheduleColors['rules'][number];
export function CalendarColorsSettings() {
  const w = useColorCopy(),
    session = useSession(),
    client = useQueryClient();
  const scope = JSON.stringify([
    session.company?.id,
    session.currentLocation?.id,
    session.user?.id,
    session.user?.role,
  ]);
  const currentScope = useRef<string | null>(scope);
  const scopeGeneration = useRef(0);
  if (currentScope.current !== scope) scopeGeneration.current++;
  currentScope.current = scope;
  useEffect(
    () => () => {
      scopeGeneration.current++;
      currentScope.current = null;
    },
    [],
  );
  const query = useQuery({
    queryKey: ['calendar-colors', session.company?.id, session.currentLocation?.id, session.user?.id],
    queryFn: ({ signal }) => api.getFresh<{ version: number; settings: ScheduleColors }>('/schedule/colors', signal),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const savedTeams = useQuery({
    queryKey: ['saved-teams'],
    queryFn: () => savedTeamsApi.list(),
    retry: false,
  });
  const activeTeams = (savedTeams.data?.items ?? []).filter((team) => team.isActive);
  const dirty = useRef(false);
  const previousScope = useRef(scope);
  const [draft, setDraft] = useState<{ version: number; settings: ScheduleColors } | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  useEffect(() => {
    const switched = previousScope.current !== scope;
    if (switched) {
      dirty.current = false;
      previousScope.current = scope;
    }
    if (!switched && (dirty.current || busy)) return;
    setDraft(query.data ?? null);
    setMessage('');
    setBusy(false);
  }, [query.data, scope]);
  const editable = session.can('company:write') && ['owner', 'manager'].includes(session.user?.role ?? '');
  async function save() {
    if (!draft) return;
    const savingScope = scope;
    const savingGeneration = scopeGeneration.current;
    setBusy(true);
    setMessage('');
    try {
      const result = await api.put<{ version: number; settings: ScheduleColors }>('/schedule/colors', {
        expectedVersion: draft.version,
        settings: draft.settings,
      });
      if (currentScope.current !== savingScope || scopeGeneration.current !== savingGeneration) return;
      dirty.current = false;
      setDraft(result);
      setMessage(w.saved);
      await client.invalidateQueries({ queryKey: ['appointments'] });
      await client.invalidateQueries({ queryKey: ['live-map-day'] });
    } catch (e) {
      if (currentScope.current === savingScope && scopeGeneration.current === savingGeneration)
        setMessage(e instanceof Error && 'code' in e && e.code === 'STALE_VERSION' ? w.stale : w.failed);
    } finally {
      if (currentScope.current === savingScope && scopeGeneration.current === savingGeneration) setBusy(false);
    }
  }
  async function reload() {
    const generation = scopeGeneration.current;
    const reloadingScope = scope;
    setBusy(true);
    const result = await query.refetch();
    if (currentScope.current !== reloadingScope || scopeGeneration.current !== generation) return;
    if (result.data && !result.error) {
      dirty.current = false;
      setDraft(result.data);
      setMessage('');
    } else setMessage(w.failed);
    setBusy(false);
  }
  function change(settings: ScheduleColors) {
    if (draft) {
      dirty.current = true;
      setDraft({ ...draft, settings });
    }
  }
  function updateRule(index: number, patch: (rule: Rule) => Rule) {
    if (draft) change({ ...draft.settings, rules: draft.settings.rules.map((r, i) => (i === index ? patch(r) : r)) });
  }
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="calendar-colors-heading">
      <div className="work-section-heading">
        <h2 id="calendar-colors-heading" className="mb-4 text-lg font-semibold">
          {w.title}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{w.hint}</p>
      {query.isPending ? <LoadingBlock label={w.loading} /> : null}
      {query.error ? (
        <div className="mb-4">
          <Alert tone="error">{w.failed}</Alert>
        </div>
      ) : null}
      {draft && query.data && (
        <fieldset disabled={!editable || busy} className="min-w-0">
          <Checkbox
            label={w.crew}
            checked={draft.settings.crewEnabled}
            onChange={(e) => change({ ...draft.settings, crewEnabled: e.target.checked })}
          />
          <div className="mt-4 space-y-4">
            {draft.settings.rules.map((rule, index) => (
              <fieldset
                key={index}
                className="min-w-0 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4"
              >
                <legend className="px-1 text-base font-semibold">
                  {index + 1}. {w[rule.type]}
                </legend>
                <div className="mb-3 flex flex-wrap gap-2">
                  {([-1, 1] as const).map((direction) => (
                    <Button
                      key={direction}
                      disabled={index + direction < 0 || index + direction >= draft.settings.rules.length}
                      onClick={() => {
                        const rules = [...draft.settings.rules];
                        [rules[index], rules[index + direction]] = [rules[index + direction]!, rules[index]!];
                        change({ ...draft.settings, rules });
                      }}
                    >
                      {direction < 0 ? w.moveUp : w.moveDown}
                    </Button>
                  ))}
                </div>
                <Select
                  label={w.ruleType}
                  value={rule.type}
                  options={COLOR_RULE_TYPES.filter(
                    (type) => type === rule.type || !draft.settings.rules.some((r) => r.type === type),
                  ).map((type) => ({ value: type, label: w[type] }))}
                  onChange={(e) =>
                    updateRule(index, (r) => ({ ...r, type: e.target.value as typeof r.type, values: [] }))
                  }
                />
                <Checkbox
                  label={w.enabled}
                  checked={rule.enabled}
                  onChange={(e) => updateRule(index, (r) => ({ ...r, enabled: e.target.checked }))}
                />
                {rule.type === 'team' ? (
                  <div className="mt-2">
                    <p className="field-hint">{w.teamHint}</p>
                  </div>
                ) : null}
                {rule.values.length > 0 ? (
                  <div className="mt-2 space-y-3">
                    {rule.values.map((value, vi) => (
                      <div
                        key={vi}
                        className="grid items-end gap-x-3 rounded-md border border-[color:var(--color-line)] bg-white p-3 sm:grid-cols-[1fr_1fr_1fr_auto]"
                      >
                        {rule.type === 'team' ? (
                          <Select
                            label={w.team_pick}
                            value={value.key}
                            disabled={!savedTeams.isSuccess}
                            error={savedTeams.isError ? w.teamsFailed : undefined}
                            options={[
                              ...(activeTeams.some((team) => normalizeColorValue(team.id) === value.key)
                                ? []
                                : [
                                    {
                                      value: value.key,
                                      label: value.label || (value.key ? w.team_inactive : w.team_choose),
                                    },
                                  ]),
                              ...activeTeams.map((team) => ({ value: normalizeColorValue(team.id), label: team.name })),
                            ]}
                            onChange={(e) => {
                              const team = activeTeams.find(
                                (entry) => normalizeColorValue(entry.id) === e.target.value,
                              );
                              updateRule(index, (r) => ({
                                ...r,
                                values: r.values.map((v, j) =>
                                  j === vi
                                    ? { ...v, key: e.target.value, label: team ? team.name.slice(0, 100) : v.label }
                                    : v,
                                ),
                              }));
                            }}
                          />
                        ) : null}
                        {(rule.type === 'team' ? (['label'] as const) : (['key', 'label'] as const)).map((field) => (
                          <TextInput
                            key={field}
                            label={w[field]}
                            maxLength={100}
                            value={value[field]}
                            onChange={(e) =>
                              updateRule(index, (r) => ({
                                ...r,
                                values: r.values.map((v, j) => (j === vi ? { ...v, [field]: e.target.value } : v)),
                              }))
                            }
                          />
                        ))}
                        <Select
                          label={w.color}
                          style={{ background: value.color, color: colorForeground(value.color) }}
                          value={value.color}
                          options={[...new Set([value.color, ...COLOR_PALETTE])].map((color) => ({
                            value: color,
                            label: color,
                          }))}
                          onChange={(e) =>
                            updateRule(index, (r) => ({
                              ...r,
                              values: r.values.map((v, j) => (j === vi ? { ...v, color: e.target.value } : v)),
                            }))
                          }
                        />
                        <div className="sm:mb-4">
                          <Button
                            variant="ghost"
                            onClick={() =>
                              updateRule(index, (r) => ({ ...r, values: r.values.filter((_, j) => j !== vi) }))
                            }
                          >
                            {w.remove}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    disabled={rule.values.length >= 100}
                    onClick={() =>
                      updateRule(index, (r) => ({
                        ...r,
                        values: [
                          ...r.values,
                          { key: r.type === 'first_visit' ? 'true' : '', label: '', color: COLOR_PALETTE[0] },
                        ],
                      }))
                    }
                  >
                    {w.addValue}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() =>
                      change({ ...draft.settings, rules: draft.settings.rules.filter((_, i) => i !== index) })
                    }
                  >
                    {w.remove}
                  </Button>
                </div>
              </fieldset>
            ))}
          </div>
          <div className="mt-4">
            <Button
              disabled={draft.settings.rules.length >= 3}
              onClick={() =>
                change({
                  ...draft.settings,
                  rules: [
                    ...draft.settings.rules,
                    {
                      type: COLOR_RULE_TYPES.find((type) => !draft.settings.rules.some((r) => r.type === type))!,
                      enabled: true,
                      values: [],
                    },
                  ],
                })
              }
            >
              {w.addRule}
            </Button>
          </div>
        </fieldset>
      )}
      {message ? (
        <div className="mt-4">
          <Alert tone={message === w.saved ? 'success' : 'error'}>{message}</Alert>
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {draft && query.data ? (
          <Button variant="primary" disabled={!editable || busy} onClick={() => void save()}>
            {w.save}
          </Button>
        ) : null}
        <Button variant="ghost" disabled={busy} onClick={() => void reload()}>
          {w.reload}
        </Button>
      </div>
    </section>
  );
}
